import { API_CONFIG, createApiUrl } from '../api'
import type { 
  ListingResponse, 
  AdminListingResponse,
  ListingsPageResponse, 
  CreateListingRequest, 
  UpdateStatusRequest,
  ListingsQuery 
} from '../../types/types.marketplace'

export class ListingsAPI {
  
  /**
   * Fetch public listings with filtering and pagination
   */
  static async fetchListings(query: ListingsQuery = {}): Promise<ListingsPageResponse> {
    const params = new URLSearchParams()
    
    // Pagination
    if (query.page !== undefined) params.append('page', query.page.toString())
    if (query.size !== undefined) params.append('size', query.size.toString())
    
    // Search
    if (query.search) params.append('search', query.search)
    
    // Sorting
    if (query.sort) {
      params.append('sort', `${query.sort.field},${query.sort.direction}`)
    }
    
    // Filters
    if (query.filters) {
      Object.entries(query.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()))
          } else {
            params.append(key, value.toString())
          }
        }
      })
    }
    
    const url = createApiUrl(API_CONFIG.ENDPOINTS.LISTINGS.PUBLIC)
    const queryString = params.toString()
    const fullUrl = queryString ? `${url}?${queryString}` : url
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch listings: ${response.statusText}`)
    }
    
    const data = await response.json()
    
    // Transform Spring Page response to our expected format
    return {
      listings: data.content || [],
      totalElements: data.totalElements || 0,
      totalPages: data.totalPages || 0,
      currentPage: data.number || 0,
      pageSize: data.size || 20,
      first: data.first || true,
      last: data.last || true
    }
  }
  
  /**
   * Get a single listing by slug
   */
  static async getListingBySlug(slug: string): Promise<ListingResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.LISTINGS.PUBLIC_BY_SLUG.replace('{slug}', slug)
    )
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Listing not found')
      }
      throw new Error(`Failed to fetch listing: ${response.statusText}`)
    }
    
    return response.json()
  }

  /**
   * Get a single public listing by ID
   */
  static async getListingById(id: string): Promise<ListingResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.LISTINGS.PUBLIC_BY_ID.replace('{id}', id)
    )

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Listing not found')
      }
      throw new Error(`Failed to fetch listing: ${response.statusText}`)
    }

    return response.json()
  }
  
  /**
   * Create a new listing (Admin only)
   */
  static async createListing(
    listing: CreateListingRequest, 
    accessToken: string
  ): Promise<AdminListingResponse> {
    const url = createApiUrl(API_CONFIG.ENDPOINTS.LISTINGS.ADMIN)
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(listing),
    })
    
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to create listing: ${error}`)
    }
    
    return response.json()
  }
  
  /**
   * Update listing status (Admin only)
   */
  static async updateListingStatus(
    listingId: string, 
    statusUpdate: UpdateStatusRequest,
    accessToken: string
  ): Promise<AdminListingResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.LISTINGS.ADMIN_UPDATE_STATUS.replace('{id}', listingId)
    )
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(statusUpdate),
    })
    
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to update listing status: ${error}`)
    }
    
    return response.json()
  }
  
  /**
   * Get admin listing by ID (Admin only)
   */
  static async getAdminListing(
    listingId: string,
    accessToken: string
  ): Promise<AdminListingResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.LISTINGS.ADMIN_BY_ID.replace('{id}', listingId)
    )
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Listing not found')
      }
      throw new Error(`Failed to fetch admin listing: ${response.statusText}`)
    }
    
    return response.json()
  }
  
  /**
   * Upload media file and get media ID
   */
  static async uploadMedia(
    file: File,
    accessToken: string
  ): Promise<{
    mediaId: string
    filename: string | null
    contentType: string | null
    objectKey: string | null
    status: 'PENDING' | 'COMPLETED' | 'FAILED'
    uploadedAt: string | null
    fileSizeBytes: number
    message?: string
  }> {
    const presignedUrlResponse = await fetch(
      createApiUrl(API_CONFIG.ENDPOINTS.LISTINGS.MEDIA_UPLOAD_PRESIGNED_URL),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
        }),
      }
    )

    if (!presignedUrlResponse.ok) {
      const error = await presignedUrlResponse.text()
      throw new Error(`Failed to request media upload URL: ${error}`)
    }

    const presignedUpload = await presignedUrlResponse.json() as {
      mediaId: string
      uploadUrl: string
      objectKey: string
      headers?: Record<string, string>
    }

    const uploadResponse = await fetch(presignedUpload.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        ...(presignedUpload.headers || {}),
      },
      body: file,
    })

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload media: ${uploadResponse.statusText}`)
    }

    const etag = uploadResponse.headers.get('etag')?.replace(/"/g, '') || ''

    const confirmResponse = await fetch(
      createApiUrl(API_CONFIG.ENDPOINTS.LISTINGS.MEDIA_UPLOAD_CONFIRM),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          mediaId: presignedUpload.mediaId,
          etag,
          fileSizeBytes: file.size,
        }),
      }
    )

    if (!confirmResponse.ok) {
      const error = await confirmResponse.text()
      throw new Error(`Failed to confirm media upload: ${error}`)
    }

    return confirmResponse.json()
  }
  
  /**
   * Attach media to listing
   */
  static async attachMediaToListing(
    listingId: string,
    mediaIds: string[],
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.LISTINGS.ATTACH_MEDIA.replace('{id}', listingId)
    )
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ mediaIds }),
    })
    
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to attach media: ${error}`)
    }
  }
}
